import { createGlobalStyle } from 'styled-components';
import { normalize } from 'styled-normalize';
import reset from 'styled-reset';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const GlobalStyle = createGlobalStyle`
  ${normalize}
  ${reset}

  /* ── Design tokens · Sandoog Brand Guideline V2 ── */
  :root {
    /* Brand — LOCKED per Brand Guideline V2. Do not substitute. */
    --brand-ink:        #090F38;  /* Sandoog corporate navy */
    --brand-ink-2:      #141B4E;  /* hover/raised on dark */
    --brand-ink-3:      #1E2660;  /* divider on dark */
    --brand-green:      #76B159;  /* Sandoog Center sub-brand accent */
    --brand-green-2:    #629948;  /* hover */
    --brand-green-3:    #4E7E37;  /* pressed */
    --brand-green-soft: #EDF5E4;  /* tinted bg for green pills */
    --brand-green-ink:  #3D6928;  /* readable text on soft bg */
    --brand-lime:       #C5E861;  /* highlight, focus rings, sidebar accent */
    --brand-yellow:     #FBB03B;  /* corporate-tier — charts/marketing only */
    --brand-gray:       #CBCACA;  /* brand neutral */

    --paper:    #FBFAF7;
    --paper-2:  #F2F0EA;
    --surface:  #FFFFFF;
    --line:     #E6E2D7;
    --line-2:   #D8D3C6;
    --ink:      #090F38;  /* body text — matches brand navy */
    --ink-2:    #2F356A;
    --muted:    #626699;  /* darkened from #6E72A0 for WCAG AA (4.5:1 on --paper) */
    --muted-2:  #A6A8C5;
    --muted-3:  #C9CAD8;

    --success:        var(--brand-green);
    --success-soft:   var(--brand-green-soft);
    --success-ink:    var(--brand-green-ink);
    /* Lighter green for status indicators — keeps --brand-green reserved for CTAs */
    --success-indicator: #9FC986;
    --success-indicator-soft: #F0F7EA;
    --warning:        #DA9213;
    --warning-soft:   #FBEDD2;
    --warning-ink:    #6E4509;
    --danger:         #D47974;  /* aligned with Sandoog Driver coral */
    --danger-soft:    #FBE6E5;
    --danger-ink:     #7A2A26;
    --info:           #4A78D6;
    --info-soft:      #E3EBFA;
    --info-ink:       #1F3F88;

    /* Chart palette — brand-led, use in order */
    --chart-1: #76B159;  /* Sandoog Center green */
    --chart-2: #4A78D6;
    --chart-3: #FBB03B;  /* Sandoog corporate yellow */
    --chart-4: #8B5CD6;
    --chart-5: #D47974;  /* Sandoog Driver coral */
    --chart-6: #2BA3A8;
    --chart-7: #BB6A2E;
    --chart-8: #6E72A0;

    /* Font stack — Neo Sans W23 is brand font (self-hosted TTF in /public/fonts/).
       DM Sans is the geometric fallback; IBM Plex Sans Arabic for Arabic content. */
    --font-sans: 'Neo Sans W23', 'DM Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
    --font-mono: 'Geist Mono', ui-monospace, 'SF Mono', Menlo, monospace;
    --font-ar:   'Neo Sans W23 Arabic', 'IBM Plex Sans Arabic', 'DM Sans', system-ui, sans-serif;
    --font-display: 'Neo Sans W23', 'DM Sans', ui-sans-serif, system-ui, sans-serif;

    --t-xs:   12px;
    --t-sm:   13px;
    --t-md:   14px;
    --t-base: 15px;
    --t-lg:   17px;
    --t-xl:   20px;
    --t-2xl:  24px;
    --t-3xl:  30px;
    --t-4xl:  38px;
    --t-5xl:  48px;

    --lh-tight:   1.1;
    --lh-snug:    1.25;
    --lh-normal:  1.4;
    --lh-relaxed: 1.6;

    --tracking-tight:   -0.02em;
    --tracking-normal:  0;
    --tracking-wide:    0.02em;
    --tracking-eyebrow: 0.16em;

    --weight-regular:  400;
    --weight-medium:   500;
    --weight-semibold: 600;
    --weight-bold:     700;

    --s-1:  4px;
    --s-2:  8px;
    --s-3:  12px;
    --s-4:  16px;
    --s-5:  20px;
    --s-6:  24px;
    --s-7:  32px;
    --s-8:  40px;
    --s-9:  48px;
    --s-10: 64px;
    --s-11: 80px;
    --s-12: 96px;

    --r-xs:   6px;
    --r-sm:   8px;
    --r-md:   10px;
    --r-lg:   14px;
    --r-xl:   18px;
    --r-2xl:  24px;
    --r-full: 9999px;

    --sh-0:          0 0 0 1px var(--line);
    --sh-1:          0 1px 2px rgba(20,18,43,0.04), 0 0 0 1px rgba(20,18,43,0.04);
    --sh-2:          0 4px 12px -4px rgba(20,18,43,0.08), 0 0 0 1px rgba(20,18,43,0.04);
    --sh-3:          0 16px 32px -16px rgba(20,18,43,0.14), 0 0 0 1px rgba(20,18,43,0.04);
    --sh-4:          0 32px 64px -24px rgba(20,18,43,0.24), 0 0 0 1px rgba(20,18,43,0.05);
    --sh-focus:      0 0 0 3px rgba(45,170,102,0.25);
    --sh-focus-ink:  0 0 0 3px rgba(20,18,43,0.18);

    --ease-out:    cubic-bezier(.16, 1, .3, 1);
    --ease-in-out: cubic-bezier(.65,.05,.36,1);
    --dur-fast: 120ms;
    --dur-base: 180ms;
    --dur-slow: 280ms;

    --z-base:    0;
    --z-raised:  10;
    --z-sticky:  20;
    --z-overlay: 50;
    --z-modal:   100;
    --z-toast:   200;

    --sidebar-w:           240px;
    --sidebar-w-collapsed: 64px;
    --topbar-h:            56px;
    --content-max:         1280px;
  }

  html {
    box-sizing: border-box;
    font-size: 16px;
    margin: 0;
    padding: 0;
  }
  body {
    margin: 0;
    padding: 0;
    font-family: var(--font-sans);
    font-weight: 400;
    font-size: var(--t-base);
    line-height: var(--lh-normal);
    color: var(--ink);
    background: var(--paper);
    overflow: hidden;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-feature-settings: 'cv11', 'ss01';
  }
  *,
  *:before,
  *:after {
    box-sizing: inherit;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  * { box-sizing: border-box; }
  button { font-family: inherit; }
  input, select, textarea { font-family: inherit; }
  svg { fill: currentColor; }

  ::-webkit-scrollbar { width: 8px; height: 8px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb {
    background: rgba(9,15,56,0.12);
    border: 2px solid transparent;
    background-clip: padding-box;
    border-radius: 999px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: rgba(9,15,56,0.22);
    background-clip: padding-box;
  }
  * { scrollbar-width: thin; scrollbar-color: rgba(9,15,56,0.12) transparent; }

  /* ── RTL overrides ── */
  [dir="rtl"] {
    font-family: var(--font-ar);
  }

  /* Table cells default to start-align in RTL */
  [dir="rtl"] th,
  [dir="rtl"] td {
    text-align: right;
  }
  [dir="rtl"] th[data-align="right"],
  [dir="rtl"] td[data-align="right"] {
    text-align: left;
  }

  /* Inputs align text to the right in RTL */
  [dir="rtl"] input[type="text"],
  [dir="rtl"] input[type="search"],
  [dir="rtl"] input[type="email"],
  [dir="rtl"] input[type="password"],
  [dir="rtl"] input[type="tel"] {
    text-align: right;
  }

  /* Mono numbers stay LTR inside RTL context */
  [dir="rtl"] .mono,
  [dir="rtl"] [class*="mono"] {
    direction: ltr;
    unicode-bidi: isolate;
  }

  ::selection { background: rgba(118,177,89,0.18); color: inherit; }

  :focus-visible {
    outline: 2px solid var(--brand-lime);
    outline-offset: 2px;
    border-radius: var(--r-xs);
  }
  :focus:not(:focus-visible) { outline: none; }

  /* Utility classes */
  .ar    { font-family: var(--font-ar); }
  .mono  { font-family: var(--font-mono); font-feature-settings: 'tnum'; }
  .tnum  { font-variant-numeric: tabular-nums; }
  .eyebrow {
    font-size: 11px;
    font-weight: var(--weight-semibold);
    letter-spacing: var(--tracking-eyebrow);
    text-transform: uppercase;
    color: var(--muted);
  }
  .h1 { font-size: var(--t-3xl); font-weight: var(--weight-bold); letter-spacing: var(--tracking-tight); line-height: var(--lh-tight); margin: 0; }
  .h2 { font-size: var(--t-2xl); font-weight: var(--weight-bold); letter-spacing: var(--tracking-tight); line-height: var(--lh-snug); margin: 0; }
  .h3 { font-size: var(--t-xl);  font-weight: var(--weight-semibold); letter-spacing: var(--tracking-tight); line-height: var(--lh-snug); margin: 0; }
  .h4 { font-size: var(--t-lg);  font-weight: var(--weight-semibold); line-height: var(--lh-snug); margin: 0; }
  .body    { font-size: var(--t-base); color: var(--ink); }
  .body-sm { font-size: var(--t-sm);  color: var(--ink); }
  .muted   { color: var(--muted); }
  .muted-2 { color: var(--muted-2); }
  .kbd {
    display: inline-flex; align-items: center; gap: 2px;
    font-family: var(--font-mono); font-size: 11px;
    padding: 2px 6px; border-radius: var(--r-xs);
    background: rgba(20,18,43,0.04);
    color: var(--muted);
    border: 1px solid rgba(20,18,43,0.06);
    border-bottom-width: 2px;
    line-height: 1;
  }
`;

export default GlobalStyle;
